import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import {
  cpGetProfileFields,
  cpUpdateProfileFields,
  type ProfileFieldConfig,
} from '@/lib/api/controlPlaneClient'

const PROFILE_FIELDS_QUERY_KEY = ['storefront', 'profile-fields']

/** Default field definitions — used when tenant has no fields configured yet. */
const DEFAULT_FIELDS: ProfileFieldConfig[] = [
  {
    fieldname: 'email',
    label: 'อีเมล',
    fieldtype: 'Email',
    required: 0,
    enabled: 0,
    sort_order: 0,
    helper_text: 'สำหรับใบเสร็จและยืนยันคำสั่งซื้อ',
  },
  {
    fieldname: 'full_name',
    label: 'ชื่อ-นามสกุล',
    fieldtype: 'Data',
    required: 0,
    enabled: 0,
    sort_order: 1,
    helper_text: 'ชื่อสำหรับการจัดส่ง',
  },
  {
    fieldname: 'birthday',
    label: 'วันเกิด',
    fieldtype: 'Date',
    required: 0,
    enabled: 0,
    sort_order: 2,
    helper_text: 'สำหรับสิทธิพิเศษวันเกิด',
  },
  {
    fieldname: 'tax_id',
    label: 'เลขประจำตัวผู้เสียภาษี',
    fieldtype: 'Data',
    required: 0,
    enabled: 0,
    sort_order: 3,
    helper_text: 'สำหรับใบกำกับภาษี',
  },
]

function FieldRow({
  field,
  onToggleEnabled,
  onToggleRequired,
  isSaving,
}: {
  field: ProfileFieldConfig
  onToggleEnabled: (fieldname: string, enabled: boolean) => void
  onToggleRequired: (fieldname: string, required: boolean) => void
  isSaving: boolean
}) {
  return (
    <div className="flex items-center justify-between px-4 py-4">
      <div className="flex-1 mr-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{field.label}</span>
          <span className="text-xs text-muted-foreground">({field.fieldname})</span>
        </div>
        {field.helper_text && (
          <p className="text-xs text-muted-foreground mt-0.5">{field.helper_text}</p>
        )}
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Required</span>
          <Switch
            checked={!!field.required && !!field.enabled}
            onCheckedChange={(checked) => onToggleRequired(field.fieldname, checked)}
            disabled={isSaving || !field.enabled}
            aria-label={`${field.label} required`}
            className="scale-90"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Enabled</span>
          <Switch
            checked={!!field.enabled}
            onCheckedChange={(checked) => onToggleEnabled(field.fieldname, checked)}
            disabled={isSaving}
            aria-label={`${field.label} enabled`}
            className="scale-90"
          />
        </div>
      </div>
    </div>
  )
}

function Divider() {
  return <div className="border-t border-border mx-4" />
}

export function ProfileFieldsSettings() {
  const queryClient = useQueryClient()

  const { data: fields, isLoading } = useQuery({
    queryKey: PROFILE_FIELDS_QUERY_KEY,
    queryFn: cpGetProfileFields,
  })

  const mutation = useMutation({
    mutationFn: cpUpdateProfileFields,
    onSuccess: (data) => {
      queryClient.setQueryData(PROFILE_FIELDS_QUERY_KEY, data)
      toast('Profile fields updated.')
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_FIELDS_QUERY_KEY })
      toast.error('Failed to save. Please try again.')
    },
  })

  // Merge server fields with defaults so all 4 always show
  const mergedFields = getMergedFields(fields ?? [])

  const handleToggleEnabled = (fieldname: string, enabled: boolean) => {
    const updated = mergedFields.map((f) =>
      f.fieldname === fieldname
        ? { ...f, enabled: enabled ? 1 : 0, required: enabled ? f.required : 0 }
        : f
    )
    // Optimistic update
    queryClient.setQueryData(PROFILE_FIELDS_QUERY_KEY, updated)
    mutation.mutate(updated)
  }

  const handleToggleRequired = (fieldname: string, required: boolean) => {
    const updated = mergedFields.map((f) =>
      f.fieldname === fieldname ? { ...f, required: required ? 1 : 0 } : f
    )
    queryClient.setQueryData(PROFILE_FIELDS_QUERY_KEY, updated)
    mutation.mutate(updated)
  }

  if (isLoading) {
    return (
      <Card className="rounded-2xl border-0">
        <CardContent className="p-4 space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="w-full h-10 rounded-md" />
          ))}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-0">
        {mergedFields.map((field, i) => (
          <div key={field.fieldname}>
            {i > 0 && <Divider />}
            <FieldRow
              field={field}
              onToggleEnabled={handleToggleEnabled}
              onToggleRequired={handleToggleRequired}
              isSaving={mutation.isPending}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

/** Merge server fields with defaults — ensures all 4 fields always appear. */
function getMergedFields(serverFields: ProfileFieldConfig[]): ProfileFieldConfig[] {
  const serverMap = new Map(serverFields.map((f) => [f.fieldname, f]))
  return DEFAULT_FIELDS.map((def) => {
    const server = serverMap.get(def.fieldname)
    return server ? { ...def, ...server } : def
  })
}
